import { useSelector, useDispatch } from 'react-redux';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, Home, Users, Building2, LogOut, Loader2, Wrench, Zap, FileText, CreditCard, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { resetLoginData } from '../../redux/action';
import { API_BASE_URL } from '../../App';

export const PanelPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { role = "", authToken = "", id = "" } = useSelector((state: any) => state.loginData || {});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    ...(role === "admin" ? [{ path: "/panel/users", label: "Users", icon: Users }] : []),
    ...(role === "admin" ? [{ path: "/panel/sites", label: "Sites", icon: Building2 }] : []),
    ...(role === "admin" || role === "staff" ? [{ path: "/panel/amc", label: "AMC", icon: Wrench }] : []),
    ...(role === "admin" || role === "staff" ? [{ path: "/panel/refilling", label: "Refilling", icon: Zap }] : []),
    ...(role === "admin" ? [{ path: "/panel/zoho-invoices", label: "Zoho Invoices", icon: FileText }] : []),
    ...(role === "admin" ? [{ path: "/panel/payments", label: "Payments", icon: CreditCard }] : []),
    ...(role === "admin" ? [{ path: "/panel/reports", label: "Reports", icon: BarChart3 }] : []),
  ];

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}account/${id}/logout`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      
      // Clear Redux state
      dispatch(resetLoginData());
      
      // Redirect to login
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still log out the user even if API fails
      dispatch(resetLoginData());
      navigate('/login');
    } finally {
      setLogoutLoading(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-screen bg-gray-50">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex md:flex-col bg-gradient-to-br from-[#02474D] via-[#034E5A] to-[#036B7A] text-white transition-all duration-300 ${
          sidebarOpen ? "md:w-72" : "md:w-24"
        } shadow-2xl`}
      >
        {/* Logo/Header */}
        <div className="p-6 border-b border-[#023A42]/50 bg-gradient-to-r from-[#02474D]/80 to-[#034E5A]/80 backdrop-blur-sm">
          <div className="flex items-center justify-between gap-3">
            <div className={`transition-all duration-300 flex items-center gap-2 ${sidebarOpen ? "opacity-100 scale-100" : "opacity-0 scale-95 hidden"}`}>
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-300 via-cyan-400 to-teal-500 flex items-center justify-center shadow-lg shadow-cyan-500/50 hover:shadow-teal-500/70 transition-shadow">
                <span className="text-white font-bold text-sm">T</span>
              </div>
              <div>
                <div className="font-bold text-2xl bg-gradient-to-r from-cyan-200 via-cyan-300 to-teal-300 bg-clip-text text-transparent">Veiligheid Samen</div>
                <div className="text-xs text-cyan-300 font-semibold">Admin Panel</div>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-[#045A66]/50 rounded-lg transition-all duration-200 hidden lg:block ml-auto group active:scale-95"
              title={sidebarOpen ? "Collapse" : "Expand"}
            >
              <Menu size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 px-4 py-8 space-y-2 overflow-y-auto scrollbar-hide relative z-10">
          <div className={`text-xs font-bold uppercase tracking-widest text-cyan-300 px-4 mb-6 transition-opacity ${sidebarOpen ? "opacity-100" : "opacity-0 hidden"}`}>
            <div className="flex items-center gap-2">
              <div className="w-1 h-3 bg-gradient-to-b from-cyan-400 to-teal-500 rounded-full"></div>
              Navigation
            </div>
          </div>
          {navItems.map((item) => {
            const IconComponent = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-300 group relative overflow-hidden ${
                  active
                    ? "bg-gradient-to-r from-cyan-500 via-cyan-400 to-teal-500 text-white shadow-xl shadow-cyan-500/40 transform scale-105"
                    : "text-cyan-200 hover:text-white hover:bg-[#045A66]/40 hover:shadow-lg hover:shadow-cyan-500/20"
                }`}
              >
                {/* Animated background for active */}
                {active && (
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-teal-400/20 group-hover:from-cyan-300/30 group-hover:to-teal-300/30 transition-all animate-pulse" />
                )}
                
                <div className={`relative z-10 transition-all ${active ? "scale-125 drop-shadow-lg" : "group-hover:scale-110"}`}>
                  <IconComponent size={22} className={active ? "drop-shadow-md" : ""} />
                </div>
                {sidebarOpen && (
                  <span className={`font-bold text-sm relative z-10 transition-all ${active ? "translate-x-0 drop-shadow" : "group-hover:translate-x-1"}`}>
                    {item.label}
                  </span>
                )}
                {active && !sidebarOpen && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-cyan-300 to-teal-500 rounded-full shadow-lg shadow-cyan-400/50" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer - Logout Button */}
        <div className="border-t border-[#023A42]/50 p-4 bg-[#021f26]/50 backdrop-blur-sm space-y-3">
          <button
            onClick={handleLogout}
            disabled={logoutLoading}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
              sidebarOpen
                ? "bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
                : "justify-center"
            } group active:scale-95 disabled:cursor-not-allowed`}
            title={!sidebarOpen ? "Logout" : ""}
          >
            {logoutLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {sidebarOpen && <span>Logging out...</span>}
              </>
            ) : (
              <>
                <LogOut size={18} />
                {sidebarOpen && <span>Logout</span>}
              </>
            )}
          </button>
          
          <div className={`text-xs text-slate-500 text-center transition-opacity ${sidebarOpen ? "opacity-100" : "opacity-0 hidden"}`}>
            <div className="font-semibold text-slate-400">Veiligheid Samen</div>
            <div>© 2025</div>
          </div>
          {!sidebarOpen && (
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-cyan-400 to-teal-600 flex items-center justify-center shadow-lg mx-auto">
              <span className="text-white font-bold text-xs">T</span>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden pb-28 md:pb-0">
        {/* Top Header Bar */}
        <header className="bg-white border-b border-gray-200 px-4 md:px-8 py-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Mobile Menu Button - Admin Only */}
              {role === "admin" && (
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-all text-gray-600 hover:text-gray-800 active:scale-95"
                  title="Menu"
                >
                  {mobileMenuOpen ? (
                    <X size={24} />
                  ) : (
                    <Menu size={24} />
                  )}
                </button>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Mobile Logout Button */}
              <button
                onClick={handleLogout}
                disabled={logoutLoading}
                className="md:hidden p-2 hover:bg-red-50 rounded-lg transition-all text-red-600 hover:text-red-700 disabled:opacity-60 active:scale-95"
                title="Logout"
              >
                {logoutLoading ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <LogOut size={20} />
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-auto">
          <div className="p-4 md:p-8 max-w-7xl mx-auto w-full">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation - Admin and Staff with Restricted Items */}
      {(role === "admin" || role === "staff") && (
        <nav className="fixed bottom-4 left-4 right-4 md:hidden bg-gradient-to-r from-[#02474D] to-[#034E5A] text-white border-t border-[#045A66] shadow-2xl shadow-cyan-500/30 rounded-full">
          <div className="flex justify-around items-center h-20 px-2">
            {navItems
              .filter((item) => {
                // Admin sees: AMC, Refilling, Payments, Reports
                if (role === "admin") {
                  return (
                    item.label === "AMC" ||
                    item.label === "Refilling" ||
                    item.label === "Payments" ||
                    item.label === "Reports"
                  );
                }
                // Staff sees: AMC, Refilling only
                if (role === "staff") {
                  return item.label === "AMC" || item.label === "Refilling";
                }
                return false;
              })
              .map((item) => {
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex flex-col items-center gap-1 px-4 py-3 transition-all duration-300 flex-1 rounded-full group relative ${
                      isActive(item.path)
                        ? "text-white bg-gradient-to-r from-cyan-500 to-teal-500 shadow-lg shadow-cyan-500/50"
                        : "text-cyan-200 hover:text-white hover:bg-[#045A66]/40"
                    }`}
                  >
                    <IconComponent size={24} className={isActive(item.path) ? "drop-shadow-lg" : "group-hover:scale-110 transition-transform"} />
                    <span className={`text-xs font-semibold transition-all ${isActive(item.path) ? "drop-shadow" : ""}`}>{item.label}</span>
                  </Link>
                );
              })}
          </div>
        </nav>
      )}

      {/* Mobile Menu Modal - Admin Only */}
      {role === "admin" && mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="absolute top-16 left-4 right-4 bg-gradient-to-br from-[#02474D] via-[#034E5A] to-[#036B7A] text-white rounded-xl shadow-2xl max-h-[calc(100vh-120px)] overflow-y-auto">
            <nav className="flex flex-col space-y-1 p-4">
              {navItems.map((item) => {
                const IconComponent = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 group ${
                      active
                        ? "bg-gradient-to-r from-cyan-500 via-cyan-400 to-teal-500 text-white shadow-lg shadow-cyan-500/40"
                        : "text-cyan-200 hover:text-white hover:bg-[#045A66]/40 hover:shadow-md hover:shadow-cyan-500/20"
                    }`}
                  >
                    <div className={`transition-all ${active ? "scale-125 drop-shadow-lg" : "group-hover:scale-110"}`}>
                      <IconComponent size={22} className={active ? "drop-shadow-md" : ""} />
                    </div>
                    <span className={`font-semibold text-sm relative z-10 transition-all ${active ? "drop-shadow" : "group-hover:translate-x-1"}`}>
                      {item.label}
                    </span>
                  </Link>
                );
              })}
              
              {/* Logout in Menu */}
              <div className="border-t border-[#045A66]/50 mt-4 pt-4">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                  disabled={logoutLoading}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-semibold transition-all duration-300 bg-red-600 hover:bg-red-700 text-white disabled:opacity-60 active:scale-95 disabled:cursor-not-allowed group"
                >
                  {logoutLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>Logging out...</span>
                    </>
                  ) : (
                    <>
                      <LogOut size={18} />
                      <span>Logout</span>
                    </>
                  )}
                </button>
              </div>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelPage;
